import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { SidebarButton } from "@/components/SidebarButton";
import type { PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import { workflowExecutionActionUrl } from "@/utils/automations";
import { type FC, useState } from "react";
import { useLocation } from "react-router-dom";
import { WorkflowExecutionActionGroupIcon } from "@/components/automations/AutomationExecutionActionGroupIcon";
import { WorkflowExecutionActionGroupLabel } from "@/components/automations/AutomationExecutionActionGroupLabel";
import IconChevronRightSmall from "central-icons/IconChevronRightSmall";
import type { ActionGroupType } from "@/utils/automation-execution-action";
import { cn } from "@/components/podkit/lib/cn";

type WorkflowExecutionActionGroupProps = {
    groupType: ActionGroupType;
    actions: PlainWorkflowExecutionAction[];
    workflowExecutionId: string;
};

export const WorkflowExecutionActionGroup: FC<WorkflowExecutionActionGroupProps> = ({
    groupType,
    actions,
    workflowExecutionId,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const location = useLocation();

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex flex-col gap-1">
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        className="flex h-8 w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left hover:bg-surface-hover"
                        data-tracking-id="toggle-action-group"
                    >
                        <IconChevronRightSmall
                            size={16}
                            className={cn("shrink-0 text-content-muted transition-transform", { "rotate-90": isOpen })}
                        />
                        <WorkflowExecutionActionGroupIcon groupType={groupType} />
                        <WorkflowExecutionActionGroupLabel groupType={groupType} count={actions.length} />
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="ml-4 flex flex-col gap-1 border-l border-border-subtle p-[1px] pl-1">
                        {actions.map((action) => {
                            const actionUrl = workflowExecutionActionUrl({
                                actionId: action.id,
                                executionId: workflowExecutionId,
                            });
                            const isActionActive = location.pathname === actionUrl;
                            const actionName = action.metadata?.actionName || "-";
                            return (
                                <SidebarButton
                                    key={action.id}
                                    to={actionUrl}
                                    label={actionName}
                                    active={isActionActive}
                                    className="text-base font-normal text-content-primary"
                                />
                            );
                        })}
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};
